import { ComponentProps, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  ListOrchestrationWorkflowsDocument,
  useCreateWorkflowMutation,
} from "@/apollo/types";
import { PrimaryButton, SecondaryButton } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import { CronInput, TimeInput } from "@/components/elements/SyncScheduler";
import {
  SyncScheduleRadioGroup,
  SyncSchedulerMenu,
  WeldSyncScheduler,
} from "@/components/modules/WeldSyncScheduler";
import { FieldErrorMessage } from "@/components/primitives/FieldErrorMessage";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { TextArea } from "@/components/primitives/input/TextArea";
import { getCronInfo } from "@/hooks/useCron";
import { useToast } from "@/providers/ToastProvider";
import mergeRefs from "@/utils/mergeRefs";

type CreateOrchestrationDialogProps = ComponentProps<typeof Modal>;

export default function CreateOrchestrationDialog({
  ...modalProps
}: CreateOrchestrationDialogProps) {
  const initialFocusRef = useRef<HTMLInputElement>(null);
  return (
    <Modal {...modalProps} initialFocus={initialFocusRef}>
      <ModalCloseButton />
      <ModalHeader>New Orchestration</ModalHeader>
      <CreateOrchestrationDialogContents
        {...modalProps}
        initialFocusRef={initialFocusRef}
      />
    </Modal>
  );
}

const CreateOrchestrationDialogContents = ({
  initialFocusRef,
  ...modalProps
}: CreateOrchestrationDialogProps & {
  initialFocusRef: React.RefObject<HTMLInputElement>;
}) => {
  const toast = useToast();

  const formMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      cronExpression: "0 0 * * *",
    },
  });

  const [createWorkflow] = useCreateWorkflowMutation({
    onCompleted: () => {
      toast(
        "Orchestration created",
        "The Orchestration has been created successfully.",
        "success",
      );
      modalProps.onClose();
    },
    onError: (error) => {
      toast(
        "Failed to create Orchestration",
        error.message || "An error occurred while creating the orchestration.",
        "error",
      );
      modalProps.onClose();
    },
    refetchQueries: [
      {
        query: ListOrchestrationWorkflowsDocument,
      },
    ],
  });

  const onSubmit = (values: {
    name: string;
    description?: string;
    cronExpression: string;
  }) => {
    return createWorkflow({
      variables: {
        name: values.name,
        description: values.description,
        cronExpression: values.cronExpression,
      },
    });
  };

  const { ref: nameInputRef, ...nameInputRegistration } = formMethods.register(
    "name",
    {
      required: "Orchestration name is required.",
    },
  );
  return (
    <FormProvider {...formMethods}>
      <ModalBody>
        <form
          id="create-orchestration-form"
          onSubmit={formMethods.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4">
            <div>
              <FieldLabel htmlFor="orchestration-name">Name</FieldLabel>
              <Input
                ref={mergeRefs(nameInputRef, initialFocusRef)}
                id="orchestration-name"
                placeholder="Orchestration name"
                {...nameInputRegistration}
              />
              <FieldErrorMessage name="name" />
            </div>
            <div>
              <FieldLabel htmlFor="orchestration-description">
                Description
              </FieldLabel>
              <TextArea
                {...formMethods.register("description")}
                id="orchestration-description"
                placeholder="A brief description of the orchestration"
              />
            </div>
            <div>
              <FieldLabel htmlFor="orchestration-schedule">Schedule</FieldLabel>
              <Controller
                name="cronExpression"
                control={formMethods.control}
                render={({ field }) => (
                  <WeldSyncScheduler
                    cron={field.value}
                    onChange={field.onChange}
                  >
                    {({ value, mode, showTimeOffsetInput }) => (
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="mb-2 text-sm text-muted-foreground">
                            How often do you want to sync data with Weld?
                          </p>
                          <CronInput />
                          <SyncScheduleRadioGroup className="grid grid-cols-2 gap-x-4 gap-y-3" />
                          {showTimeOffsetInput && (
                            <>
                              <p className="mb-2 mt-4 text-sm text-muted-foreground">
                                Starting at
                              </p>
                              <TimeInput />
                            </>
                          )}
                        </div>
                        <div>
                          <FieldLabel>Schedule description:</FieldLabel>
                          <div className="flex items-center gap-4 text-sm">
                            <span>
                              {value && (
                                <CronDisplayReadable
                                  verbose
                                  cron={value.value}
                                />
                              )}
                              {!value && mode === "custom" && (
                                <span>
                                  Write a cron expression to see update
                                  frequency here
                                </span>
                              )}
                            </span>
                            <SyncSchedulerMenu />
                          </div>
                        </div>
                      </div>
                    )}
                  </WeldSyncScheduler>
                )}
                rules={{
                  validate: (value) => {
                    if (!value) {
                      return "Cron expression is required.";
                    }
                    const { isValid } = getCronInfo(value);
                    if (!isValid) {
                      return "Invalid cron expression.";
                    }
                    return true;
                  },
                }}
              />
              <FieldErrorMessage name="cronExpression" />
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton
          type="submit"
          form="create-orchestration-form"
          disabled={
            formMethods.formState.isSubmitting || !formMethods.formState.isValid
          }
          isLoading={formMethods.formState.isSubmitting}
          loadingText="Saving..."
        >
          Create Orchestration
        </PrimaryButton>
        <SecondaryButton
          disabled={formMethods.formState.isSubmitting}
          onClick={() => {
            modalProps.onClose();
          }}
        >
          Cancel
        </SecondaryButton>
      </ModalFooter>
    </FormProvider>
  );
};
